<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import interactjs from "interactjs";
import debounce from "lodash.debounce";
import { getImageInfo } from "../utils/preload-image";
import { MODES, SURFACES } from "../misc/constants";

const emit = defineEmits([
  "update-downscale-factor",
  "update-neck-line-height",
  "end-neck-line-height"
]);

const props = defineProps({
  surface: {
    type: Object,
    required: true
  },
  surfaceLabel: {
    type: String,
    required: true
  },
  shownCanvasBorder: {
    type: Boolean,
    required: false,
    default: false
  },
  downscaleFactor: {
    type: Number,
    required: true
  },
  getSurface: {
    type: Function,
    required: true
  },
  shownCustomNeckLines: {
    type: Boolean,
    required: false,
    default: false
  },
  currentNeckLinePx: {
    type: Number,
    required: false,
    default: 0
  },
  maxBoundingsHeight: {
    type: Number,
    required: false,
    default: 0
  }
});

const store = useStore();
const mode = computed(() => store.getters["app/mode"]);

const surfaceReference = ref(null);
const boxReference = ref(null);
const canvasStyles = ref({});
const interact = ref(null);

const getSurface = () =>
  props.surfaceLabel === SURFACES.FRONT_LEFT_CHEST
    ? props.getSurface(SURFACES.FRONT)
    : props.surface;

const boundingBoxStyles = computed(() => {
  const { left, width, height } = props.surface.bounding_box;
  const { top_y: topY } = getSurface().surface_location_offset;
  const maxHeight = Math.min(props.maxBoundingsHeight, height * props.downscaleFactor);
  return {
    top: `${props.currentNeckLinePx + topY * props.downscaleFactor}px`,
    left: `${left * props.downscaleFactor}px`,
    width: `${width * props.downscaleFactor}px`,
    height: `${maxHeight}px`
  };
});

const initDrag = () => {
  interact.value = interactjs(boxReference.value).draggable({
    listeners: {
      move(event) {
        emit("update-neck-line-height", props.currentNeckLinePx + event.dy);
      },
      end(event) {
        emit("end-neck-line-height", props.currentNeckLinePx + event.dy);
      }
    }
  });
};

const initSurface = async () => {
  const { width: imageWidth } = await getImageInfo(props.surface.picture);
  canvasStyles.value = {};
  nextTick(() => {
    const { width, height, top, left } = getSurface().bounding_box;
    const { width: canvasWidth = 0, height: canvasHeight = 0 } = surfaceReference.value || {};
    const newDownscaleFactor = Math.min(canvasWidth / width, canvasHeight / height);
    const offsetTop = (top * newDownscaleFactor) / 2;

    canvasStyles.value = {
      width: `${imageWidth * newDownscaleFactor}px`,
      top: `${-offsetTop}px`,
      left: `${-left * newDownscaleFactor}px`,
      height: `${offsetTop + height * newDownscaleFactor}px`,
      minHeight: "auto"
    };

    emit("update-downscale-factor", newDownscaleFactor);
  });
};

let prevInnerWidth;
const resizeSurface = debounce(
  () => {
    const currentInnerWidth = window.innerWidth;
    if (currentInnerWidth === prevInnerWidth) return;
    initSurface();
    prevInnerWidth = currentInnerWidth;
  },
  1000 // 1 second debounce
);

watch(
  () => props.shownCustomNeckLines,
  shown => {
    interact.value?.unset();
    if (shown) initDrag();
  }
);

watch(
  () => mode.value,
  newMode => newMode === MODES.MADE && initSurface()
);

onMounted(() => {
  initSurface();
  window.addEventListener("resize", resizeSurface);

  if (props.shownCustomNeckLines) initDrag();
});
onUnmounted(() => {
  interact.value?.unset();
  window.removeEventListener("resize", resizeSurface);
});
</script>

<template>
  <div class="artwork-canvas">
    <div class="artwork-canvas__height-wrapper" :style="canvasStyles">
      <img
        ref="surfaceReference"
        :src="props.surface.picture"
        class="artwork-canvas__canvas"
        alt="Artwork canvas"
      />

      <slot name="measurement" />

      <div
        ref="boxReference"
        class="artwork-canvas__bounding-box"
        :class="{
          'artwork-canvas__bounding-box--border': props.shownCanvasBorder,
          'artwork-canvas__bounding-box--move': props.shownCustomNeckLines
        }"
        :style="boundingBoxStyles"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";

.artwork-canvas {
  overflow: hidden;
  padding: 0 50px 200px;
  background: $white;

  &__height-wrapper {
    position: relative;
    min-height: 330px;
  }

  &__canvas {
    display: block;
    max-width: 100%;
    user-select: none;
  }

  &__bounding-box {
    position: absolute;
    pointer-events: none;
    touch-action: none;

    &--border {
      outline: 2px dashed $pod-premium-bg;
    }

    &--move {
      cursor: move;
      pointer-events: all;
    }
  }
}
</style>
